import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CheckCircle } from '@mui/icons-material';
import {
    fetchPaymentRequestDetails,
    isPaymentRequestSettled,
    resolvePaymentRequestStatus
} from '../../services/paymentRequestService';
import { matchesPaymentRequestRef, isPaymentApprovedStatus } from '../../utils/transactionWebSocket';

const POLL_MS = 3000;

/**
 * Modal attente confirmation client (WebSocket + polling de secours).
 */
const WaitingPaymentModal = ({
    open,
    requestId,
    amount,
    currency = 'CDF',
    onClose,
    onApproved
}) => {
    const [status, setStatus] = useState('pending');
    const approvedRef = useRef(false);
    const onApprovedRef = useRef(onApproved);
    const requestIdRef = useRef(requestId);
    const sessionKeyRef = useRef('');

    onApprovedRef.current = onApproved;
    requestIdRef.current = requestId;

    const markApproved = useCallback(() => {
        if (approvedRef.current) return;
        approvedRef.current = true;
        setStatus('approved');
        onApprovedRef.current?.();
    }, []);

    useEffect(() => {
        if (!open || !requestId) {
            if (!open) {
                sessionKeyRef.current = '';
            }
            return;
        }

        const sessionKey = String(requestId);
        if (sessionKeyRef.current !== sessionKey) {
            sessionKeyRef.current = sessionKey;
            approvedRef.current = false;
            setStatus('pending');
        }

        const handleWsEvent = (e) => {
            const { ref, status: st } = e.detail || {};
            const id = requestIdRef.current;
            if (!ref || !id) return;
            if (matchesPaymentRequestRef(ref, id) && isPaymentApprovedStatus(st)) {
                markApproved();
            }
        };

        window.addEventListener('paymentRequest:approved', handleWsEvent);
        window.addEventListener('transaction:listener', handleWsEvent);

        return () => {
            window.removeEventListener('paymentRequest:approved', handleWsEvent);
            window.removeEventListener('transaction:listener', handleWsEvent);
        };
    }, [open, requestId, markApproved]);

    useEffect(() => {
        if (!open || !requestId || approvedRef.current) return;

        const poll = async () => {
            if (approvedRef.current) return;
            try {
                const details = await fetchPaymentRequestDetails(requestIdRef.current);
                const st = resolvePaymentRequestStatus(details);
                if (isPaymentRequestSettled(st)) {
                    markApproved();
                }
            } catch {
                /* prochain poll */
            }
        };

        poll();
        const intervalId = setInterval(poll, POLL_MS);
        return () => clearInterval(intervalId);
    }, [open, requestId, markApproved]);

    const handleClose = useCallback(() => {
        if (status === 'pending') {
            const ok = window.confirm(
                "Vous pouvez continuer ailleurs : vous serez notifié quand le client aura confirmé le paiement. Fermer cette fenêtre ?"
            );
            if (!ok) return;
        }
        onClose?.();
        approvedRef.current = false;
        setStatus('pending');
        sessionKeyRef.current = '';
    }, [status, onClose]);

    if (!open || !requestId) return null;

    const isApproved = status === 'approved';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center border border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                    Paiement {isApproved ? 'confirmé' : 'en attente'}
                </h2>
                <p className="text-2xl font-bold text-amber-800 my-4">
                    {amount} {currency}
                </p>
                <p className="text-slate-600 text-sm mb-6">
                    {isApproved
                        ? 'Le client vient de confirmer le paiement. La liste va se rafraîchir.'
                        : 'Le client doit confirmer le paiement sur son téléphone. Cette fenêtre se met à jour automatiquement.'}
                </p>
                {isApproved ? (
                    <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" aria-hidden />
                ) : (
                    <div
                        className="w-14 h-14 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                        role="status"
                        aria-label="En attente de confirmation"
                    />
                )}
                <p className="text-sm text-slate-500 mb-6">
                    {isApproved ? 'Transaction enregistrée' : 'En attente de confirmation…'}
                </p>
                <button
                    type="button"
                    onClick={handleClose}
                    className="w-full py-3 rounded-xl border-2 border-amber-500 text-amber-700 font-medium hover:bg-amber-50 transition-colors"
                >
                    {isApproved ? 'Fermer' : 'Quitter (garder la demande active)'}
                </button>
            </div>
        </div>
    );
};

export default WaitingPaymentModal;
